import React, { useState, useMemo } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import CallDetails from '../callHistory/callDetails';
import CallTable from '../callHistory/callLogs';
import { useWeek } from '../../contexts/WeekContext';
import { startOfDay, endOfDay, isWithinInterval } from 'date-fns';
import { parseCustomTimestamp } from '../../utils/dateUtils';

/**
 * CallsTab Component
 * 
 * Displays filtered call results in search.
 * Uses CallTable and CallDetails which automatically adapt based on client:
 * - For celeste/helios (admin view): Shows 'Call Id' column and 3 tabs (no Order Details)
 * - For other clients: Shows 'Equipment' column and 4 tabs (includes Order Details)
 */
const CallsTab = ({ 
  calls, 
  searchQuery, 
  selectedCall, 
  setSelectedCall 
}) => {
  // State for selected rows (required by CallTable)
  const [selectedRows, setSelectedRows] = useState([]);
  const { dateRange } = useWeek();

  // Filter calls by date range from WeekPicker
  const filteredCalls = useMemo(() => {
    if (!calls || calls.length === 0) return [];
    if (!dateRange?.start || !dateRange?.end) return calls;

    const rangeStart = startOfDay(dateRange.start);
    const rangeEnd = endOfDay(dateRange.end);

    return calls.filter(call => {
      const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
      if (!callDate) return false;
      return isWithinInterval(callDate, { start: rangeStart, end: rangeEnd });
    });
  }, [calls, dateRange]);

  console.log('CallsTab render:', { callsLength: calls?.length, filteredCallsLength: filteredCalls?.length, calls: calls });

  if (!filteredCalls || filteredCalls.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon icon="material-symbols:search-off" className="text-6xl text-bgAccent mb-4" />
          <p className="text-font-secondary text-lg">No calls found for "{searchQuery}"</p>
          {dateRange?.start && dateRange?.end && (
            <p className="text-font-secondary text-sm mt-2">
              in the selected date range
            </p>
          )}
        </div>
      </div>
    );
  }

  const handleCloseCallDetails = () => {
    setSelectedCall(null);
  };

  return (
    <div className="flex flex-1 gap-2 w-full min-h-0 overflow-hidden flex-col">
      {/* Breadcrumbs - shown when call details are open */}
      {selectedCall && (
        <div className="flex items-center gap-1 text-font-primary mb-2 flex-shrink-0">
          <button
            onClick={handleCloseCallDetails}
            className="flex items-center justify-center rounded-lg hover:bg-bgAccent transition-colors cursor-pointer"
            title="Go back to calls"
          >
            <Icon icon="mdi:chevron-left" className="text-3xl text-accent hover:text-primary transition-colors" />
          </button>
          <span 
            className="flex items-center justify-center gap-1 group transition-colors cursor-pointer"
            onClick={handleCloseCallDetails}
          >
            <Icon icon="material-symbols-light:call-log" className="text-xl text-font-secondary group-hover:text-primary transition-colors" />
            <span className="text-font-secondary group-hover:text-font-primary transition-colors">Calls</span>
          </span>
          <Icon icon="mdi:chevron-right" className="text-lg text-font-secondary" />
          <span className="flex items-center justify-center gap-1">
            <Icon icon="material-symbols:info-outline" className="text-2xl text-primary" />
            <span className="text-font-primary text-base">
              Call Details
            </span>
          </span>
        </div>
      )}

      <div className="flex flex-1 gap-2 w-full min-h-0 overflow-hidden">
        {/* Calls List */}
        <div className={`flex flex-col w-full transition-all duration-300 ease-out overflow-hidden
          ${selectedCall ? 'hidden 2xl:flex 2xl:w-1/2' : 'w-full'}
        `}>
          <CallTable
            onRowClick={setSelectedCall}
            isShort={!!selectedCall}
            callRow={selectedCall?.metadata.stream_sid}
            displayData={filteredCalls}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            className="w-full h-full flex-1 min-h-0"
          />
        </div>

        {/* Call Details Panel */}
        {selectedCall && (
          <div className="w-full flex flex-col 2xl:w-1/2 flex-shrink-0 min-h-0 max-h-screen overflow-hidden">
            <CallDetails call={selectedCall} onClose={handleCloseCallDetails} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CallsTab;
